import React, { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from './ChartLayout';
import { KeyMetric, KeyMetrics } from './KeyMetrics';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';

interface EmploymentSectionProps {
  location: string;
  geofips?: string | null;
  embedOnly?: boolean;
}

interface EmploymentData {
  year: number;
  total_worker: number;
}

interface EmploymentMetrics {
  currentWorkers: number | null;
  latestYear: number | null;
  growthRate: number | null;
  peakWorkers: number | null;
  peakYear: number | null;
}

const tableMap: Record<string, string> = {
  workplace: 'lodes_employees_workplace',
  sex: 'lodes_employees_workplace_sex',
  race: 'lodes_employees_workplace_race',
  income: 'lodes_employees_workplace_income',
  sector: 'lodes_employees_workplace_sector', // Uses S-codes
};

const tabTitleMap: Record<string, string> = {
  workplace: 'Employment Trends',
  sex: 'Employment by Sex',
  race: 'Employment by Race',
  income: 'Employment by Income',
  sector: 'Employment by Sector',
};

const EmploymentSection: React.FC<EmploymentSectionProps> = ({ location, geofips, embedOnly = false }) => {
  const [activeTab, setActiveTab] = useState<'workplace' | 'sex' | 'race' | 'income' | 'sector'>('workplace');
  const [activeView, setActiveView] = useState<'overview' | 'relevance' | 'equity' | 'methodology'>('overview');
  const [chartType, setChartType] = useState<'line' | 'area' | 'percentage'>('line');
  const [chartData, setChartData] = useState<any>(null);
  const [metrics, setMetrics] = useState<EmploymentMetrics>({
    currentWorkers: null,
    latestYear: null,
    growthRate: null,
    peakWorkers: null,
    peakYear: null,
  });
  const [employmentSeries, setEmploymentSeries] = useState<EmploymentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUsingCountyData, setIsUsingCountyData] = useState(false);
  const [countyName, setCountyName] = useState<string | null>(null);
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);

  // Fetch data based on active tab
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const tableName = tableMap[activeTab];
        if (!tableName) return;

        // Get geofips if not provided
        let locationGeofips: string | null = geofips ? String(geofips) : null;
        let geographyType: string = 'place';
        
        if (!locationGeofips) {
          const geofipsNum = await supabaseLocationService.getGeofips(location);
          locationGeofips = geofipsNum ? String(geofipsNum) : null;
        }

        if (!locationGeofips) {
          throw new Error(`Could not find geofips for location: ${location}`);
        }

        // LODES data is available at place, county, and state levels
        // For cities, first try to find place-level data, then fall back to county-level
        // First, try to get location info to determine if it's a city
        let queryGeofips = locationGeofips;
        
        try {
          const { data: locationInfo, error: locationError } = await supabaseClient
            .from('population')
            .select('geography_type, county')
            .eq('geofips', parseInt(locationGeofips))
            .limit(1)
            .single();

          if (!locationError && locationInfo) {
            if (locationInfo.geography_type === 'City') {
              // First, try to find place-level data for the city
              // Use the city's geofips directly (it should be 7 digits, but we'll try both formats)
              const cityGeofips7 = String(parseInt(locationGeofips)).padStart(7, '0');
              const cityGeofips6 = cityGeofips7.slice(-6);
              
              // Try querying with place geography_type first
              const { data: placeData, error: placeError } = await supabaseClient
                .from(tableName)
                .select('geofips')
                .eq('geofips', cityGeofips6)
                .eq('geography_type', 'place')
                .limit(1)
                .single();
              
              if (!placeError && placeData) {
                // Place-level data exists!
                queryGeofips = cityGeofips6;
                geographyType = 'place';
                setIsUsingCountyData(false);
                setCountyName(null);
                console.log(`EmploymentSection: Using place-level data for city ${location}: geofips=${queryGeofips}`);
              } else {
                // Fall back to county-level data
                const countyName = locationInfo.county || `${location} County`;
                const { data: countyData, error: countyError } = await supabaseClient
                  .from('population')
                  .select('geofips')
                  .eq('location_name', countyName)
                  .eq('geography_type', 'County')
                  .limit(1)
                  .single();
                
                if (!countyError && countyData && countyData.geofips) {
                  queryGeofips = String(countyData.geofips);
                  geographyType = 'county';
                  setIsUsingCountyData(true);
                  setCountyName(countyName);
                  console.log(`EmploymentSection: Using county-level data for city ${location}: county geofips=${queryGeofips}`);
                } else {
                  setIsUsingCountyData(false);
                  setCountyName(null);
                }
              }
            } else if (locationInfo.geography_type === 'County') {
              geographyType = 'county';
            } else if (locationInfo.geography_type === 'State') {
              geographyType = 'state';
              // State geofips is typically "6" for California
              queryGeofips = '6';
            }
          }
        } catch (err) {
          console.warn('EmploymentSection: Could not determine geography type, defaulting to place:', err);
          // If we can't determine, try with the original geofips as place
        }

        // Build query based on table type
        let query = supabaseClient
          .from(tableName)
          .select('*')
          .eq('geofips', queryGeofips)
          .eq('geography_type', geographyType)
          .order('year', { ascending: true });

        const { data, error: queryError } = await query;
        
        if (queryError) throw queryError;
        if (!data || data.length === 0) {
          setLoading(false);
          return;
        }

        // For tables with additional dimensions, create separate datasets
        if (activeTab === 'sex' || activeTab === 'race' || activeTab === 'income' || activeTab === 'sector') {
          // Group by dimension and year
          const dimensionKey = activeTab === 'sex' ? 'sex' : 
                              activeTab === 'race' ? 'race' : 
                              activeTab === 'income' ? 'income' : 'sector';
          
          // Filter data: sex and race tabs should only show data from 2009 onwards
          const minYear = (activeTab === 'sex' || activeTab === 'race') ? 2009 : 2002;
          const filteredData = data.filter((row: any) => row.year >= minYear);
          
          // For sector tab with S-codes, aggregate NAICS sectors into S-codes
          if (activeTab === 'sector') {
            // Map NAICS sectors to S-codes
            const naicsToSCode: Record<string, string> = {
              'NAICS sector 11 (Agriculture, Forestry, Fishing and Hunting)': 'S001',
              'NAICS sector 21 (Mining, Quarrying, and Oil and Gas Extraction)': 'S001',
              'NAICS sector 23 (Construction)': 'S001',
              'NAICS sector 31-33 (Manufacturing)': 'S001',
              'NAICS sector 22 (Utilities)': 'S002',
              'NAICS sector 42 (Wholesale Trade)': 'S002',
              'NAICS sector 44-45 (Retail Trade)': 'S002',
              'NAICS sector 48-49 (Transportation and Warehousing)': 'S002',
              'NAICS sector 51 (Information)': 'S003',
              'NAICS sector 52 (Finance and Insurance)': 'S004',
              'NAICS sector 53 (Real Estate and Rental and Leasing)': 'S004',
              'NAICS sector 54 (Professional, Scientific, and Technical Services)': 'S005',
              'NAICS sector 55 (Management of Companies and Enterprises)': 'S005',
              'NAICS sector 56 (Administrative and Support and Waste Management and Remediation Services)': 'S005',
              'NAICS sector 61 (Educational Services)': 'S006',
              'NAICS sector 62 (Health Care and Social Assistance)': 'S006',
              'NAICS sector 71 (Arts, Entertainment, and Recreation)': 'S007',
              'NAICS sector 72 (Accommodation and Food Services)': 'S007',
              'NAICS sector 81 (Other Services [except Public Administration])': 'S008',
              'NAICS sector 92 (Public Administration)': 'S008',
            };
            
            const sCodeLabels: Record<string, string> = {
              'S000': 'All sectors (total employment)',
              'S001': 'Goods producing',
              'S002': 'Trade, transportation & utilities',
              'S003': 'Information',
              'S004': 'Financial activities',
              'S005': 'Professional & business services',
              'S006': 'Education & health services',
              'S007': 'Leisure & hospitality',
              'S008': 'Other services',
            };
            
            // Aggregate NAICS sectors into S-codes
            const sCodeGrouped: Record<string, Record<number, number>> = {};
            const allYears = new Set<number>();
            
            filteredData.forEach((row: any) => {
              const naicsSector = row[dimensionKey] || 'Unknown';
              const sCode = naicsToSCode[naicsSector];
              
              if (sCode) {
                const year = row.year;
                const workers = row.total_worker || 0;
                
                if (!sCodeGrouped[sCode]) {
                  sCodeGrouped[sCode] = {};
                }
                sCodeGrouped[sCode][year] = (sCodeGrouped[sCode][year] || 0) + workers;
                allYears.add(year);
              }
            });
            
            // Convert to chart format (excluding S000 total)
            const years = Array.from(allYears).sort((a, b) => a - b);
            const datasets = Object.keys(sCodeGrouped)
              .filter(sCode => sCode !== 'S000') // Exclude S000 total
              .sort()
              .map((sCode, idx) => {
                const color = [
                  'rgba(59, 130, 246, 1)',   // Blue
                  'rgba(16, 185, 129, 1)',   // Green
                  'rgba(245, 158, 11, 1)',    // Yellow
                  'rgba(239, 68, 68, 1)',    // Red
                  'rgba(139, 92, 246, 1)',   // Purple
                  'rgba(236, 72, 153, 1)',   // Pink
                  'rgba(34, 197, 94, 1)',    // Emerald
                  'rgba(251, 146, 60, 1)',   // Orange
                  'rgba(99, 102, 241, 1)',   // Indigo
                ][idx % 9];
                
                return {
                  label: sCodeLabels[sCode] || sCode,
                  data: years.map(year => sCodeGrouped[sCode][year] || 0),
                  borderColor: color,
                  backgroundColor: color.replace('1)', '0.1)'),
                  borderWidth: 2,
                  fill: false,
                  tension: 0.1,
                };
              });

            setChartData({ labels: years.map(y => y.toString()), datasets });
            setLoading(false);
            return;
          }
          
          const grouped: Record<string, Record<number, number>> = {};
          const allYears = new Set<number>();
          
          filteredData.forEach((row: any) => {
            const dimension = row[dimensionKey] || 'Unknown';
            const year = row.year;
            const workers = row.total_worker || 0;
            
            if (!grouped[dimension]) {
              grouped[dimension] = {};
            }
            grouped[dimension][year] = (grouped[dimension][year] || 0) + workers;
            allYears.add(year);
          });

          // Convert to chart format with multiple datasets
          const years = Array.from(allYears).sort((a, b) => a - b);
          const datasets = Object.keys(grouped).map((dimension, idx) => {
            const color = [
              'rgba(59, 130, 246, 1)',   // Blue
              'rgba(16, 185, 129, 1)',   // Green
              'rgba(245, 158, 11, 1)',    // Yellow
              'rgba(239, 68, 68, 1)',    // Red
              'rgba(139, 92, 246, 1)',   // Purple
              'rgba(236, 72, 153, 1)',   // Pink
            ][idx % 6];
            
            return {
              label: dimension,
              data: years.map(year => grouped[dimension][year] || 0),
              borderColor: color,
              backgroundColor: color.replace('1)', '0.1)'),
              borderWidth: 2,
              fill: false,
              tension: 0.1,
            };
          });

          setChartData({ labels: years.map(y => y.toString()), datasets });
        } else {
          // Simple tables (workplace) - employment only
          // No filtering needed for these tabs
          const chartDataPoints: EmploymentData[] = data.map((row: any) => ({
            year: row.year,
            total_worker: row.total_worker || 0,
          }));

          setEmploymentSeries(chartDataPoints);
          const allYears = Array.from(new Set<number>(chartDataPoints.map((d) => d.year))).sort((a, b) => a - b);
          const employmentByYear = new Map(chartDataPoints.map((d) => [d.year, d.total_worker]));

          setChartData({
            labels: allYears.map((y) => y.toString()),
            datasets: [
              {
                label: 'Total Workers',
                data: allYears.map((year) => employmentByYear.get(year) ?? null),
                borderColor: 'rgba(59, 130, 246, 1)',
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                borderWidth: 2,
                fill: chartType === 'area',
                tension: 0.1,
                pointRadius: 3,
                pointHoverRadius: 5,
                yAxisID: 'y',
              },
            ],
          });
        }

        setLoading(false);
      } catch (err: any) {
        console.error('Error fetching employment data:', err);
        setError(err.message || 'Failed to load employment data');
        setLoading(false);
      }
    };

    fetchData();
  }, [location, geofips, activeTab, chartType]);

  // Calculate metrics when chartData changes
  useEffect(() => {
    if (!chartData) return;

    // Handle multi-dataset vs single dataset
    let dataPoints: EmploymentData[] = [];
    
    if (activeTab === 'workplace') {
      dataPoints = employmentSeries;
    } else if (Array.isArray(chartData)) {
      dataPoints = chartData;
    } else if (chartData.datasets && chartData.labels) {
      // For multi-dataset, sum across all dimensions for each year
      const years = chartData.labels.map((y: string) => parseInt(y));
      dataPoints = years.map((year: number) => {
        const yearIdx = years.indexOf(year);
        const total = chartData.datasets.reduce((sum: number, dataset: any) =>
          sum + (dataset.data[yearIdx] || 0), 0
        );
        return { year, total_worker: total };
      });
    }

    if (dataPoints.length === 0) return;

    const latest = dataPoints[dataPoints.length - 1];
    const first = dataPoints[0];
    const peak = dataPoints.reduce((max: any, curr: any) => 
      curr.total_worker > max.total_worker ? curr : max
    );

    const growthRate = first.total_worker > 0
      ? ((latest.total_worker - first.total_worker) / first.total_worker) * 100
      : 0;

    setMetrics({
      currentWorkers: latest.total_worker,
      latestYear: latest.year,
      growthRate: growthRate,
      peakWorkers: peak.total_worker,
      peakYear: peak.year,
    });
  }, [chartData, activeTab, employmentSeries]);

  // Create chart
  useEffect(() => {
    if (!chartRef.current || !chartData) return;

    // Check if chartData is array (simple) or object (multi-dataset)
    const isMultiDataset = chartData && !Array.isArray(chartData) && chartData.datasets;
    if (!isMultiDataset && (!Array.isArray(chartData) || chartData.length === 0)) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    let chartConfig: any;
    let yAxisConfig: any;
    let tooltipCallbacks: any;
    const titleText = `${tabTitleMap[activeTab] || 'Employment'} - ${location}`;

    if (isMultiDataset && activeTab !== 'workplace') {
      // Multi-dataset chart (sex, race, income, sector)
      const originalData = chartData;
      let processedData = { ...originalData };
      
      if (chartType === 'percentage') {
        // Normalize to percentages (sum to 100% for each year)
        const labels = originalData.labels;
        const datasets = originalData.datasets.map((dataset: any) => {
          const percentages = labels.map((_: any, yearIdx: number) => {
            const yearTotal = originalData.datasets.reduce((sum: number, d: any) => 
              sum + (d.data[yearIdx] || 0), 0
            );
            return yearTotal > 0 ? (dataset.data[yearIdx] || 0) / yearTotal * 100 : 0;
          });
          return {
            ...dataset,
            data: percentages,
          };
        });
        processedData = {
          ...originalData,
          datasets,
        };
        
        yAxisConfig = {
          beginAtZero: true,
          max: 100,
          title: {
            display: true,
            text: 'Percentage (%)',
          },
          ticks: {
            callback: (value: any) => `${value}%`,
          },
        };
        
        tooltipCallbacks = {
          label: (context: any) => {
            const value = context.parsed.y;
            return `${context.dataset.label}: ${value.toFixed(1)}%`;
          },
        };
      } else {
        yAxisConfig = {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Number of Workers',
          },
          ticks: {
            callback: (value: any) => value.toLocaleString(),
          },
        };
        
        tooltipCallbacks = {
          label: (context: any) => {
            return `${context.dataset.label}: ${context.parsed.y.toLocaleString()}`;
          },
        };
      }

      // Update dataset properties based on chart type
      const datasets = processedData.datasets.map((dataset: any) => {
        const baseConfig = {
          ...dataset,
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5,
        };

        if (chartType === 'area' || chartType === 'percentage') {
          return {
            ...baseConfig,
            fill: true,
            tension: 0.1,
          };
        } else {
          return {
            ...baseConfig,
            fill: false,
            tension: 0.1,
          };
        }
      });

      chartConfig = {
        type: 'line',
        data: {
          ...processedData,
          datasets,
        },
      };
      
      // Update yAxisConfig with stacking
      yAxisConfig = {
        ...yAxisConfig,
        stacked: chartType === 'area' || chartType === 'percentage',
      };
    } else {
      // Workplace chart (employment only)
      chartConfig = {
        type: 'line',
        data: chartData,
      };

      yAxisConfig = {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Number of Workers',
          },
          ticks: {
            callback: (value: any) => value.toLocaleString(),
          },
        },
      };

      tooltipCallbacks = {
        label: (context: any) => {
          const value = context.parsed.y;
          if (value === null || value === undefined) return '';
          return `${context.dataset.label}: ${value.toLocaleString()}`;
        },
      };
    }

    chartInstance.current = new Chart(ctx, {
      ...chartConfig,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: standardLegend,
          title: standardTitle(titleText),
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: tooltipCallbacks,
          },
        },
        scales: activeTab === 'workplace' ? {
          ...yAxisConfig,
          x: {
            title: {
              display: true,
              text: 'Year',
            },
          },
        } : {
          y: yAxisConfig,
          x: {
            title: {
              display: true,
              text: 'Year',
            },
            stacked: isMultiDataset && (chartType === 'area' || chartType === 'percentage'),
          },
        },
      },
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [chartData, chartType, activeTab, location]);

  const getTabTitle = () => {
    return tabTitleMap[activeTab] || 'Employment';
  };

  const getOverviewContent = () => {
    // Determine the start year based on the active tab
    const startYear = activeTab === 'workplace' ? 1990 : (activeTab === 'sex' || activeTab === 'race') ? 2009 : 2002;
    
    // Check if we're using county-level data for a city (fallback when place data not available)
    const isCityUsingCountyData = location && !location.includes('County') && !location.includes('California') && isUsingCountyData;
    const dataLevelNote = isCityUsingCountyData 
      ? ` Note: City-level employment data is not available for ${location} in LODES, so this chart displays county-level data for ${countyName || 'the county'}.`
      : '';
    
    const baseText = `This chart displays ${getTabTitle().toLowerCase()} for ${location} from ${startYear} to 2022 using LEHD Origin-Destination Employment Statistics (LODES8).${dataLevelNote} `;
    
    if (metrics.currentWorkers && metrics.latestYear) {
      const growthBaseYear = startYear;
      const locationLabel = isCityUsingCountyData ? 'the county' : location;
      return baseText + `As of ${metrics.latestYear}, ${locationLabel} had approximately ${metrics.currentWorkers.toLocaleString()} workers${metrics.growthRate ? `, representing a ${metrics.growthRate > 0 ? 'growth' : 'decline'} of ${Math.abs(metrics.growthRate).toFixed(1)}% since ${growthBaseYear}` : ''}.`;
    }
    return baseText;
  };

  const getRelevanceContent = () => {
    let tabSpecific = '';
    if (activeTab === 'workplace') {
      tabSpecific = ' Workplace employment data shows where people work, helping identify job centers and economic activity patterns.';
    } else if (activeTab === 'sex') {
      tabSpecific = ' Employment by sex data highlights gender disparities in the workforce, supporting equity-focused policy development.';
    } else if (activeTab === 'race') {
      tabSpecific = ' Employment by race/ethnicity data reveals disparities in economic opportunity, critical for addressing systemic inequities and ensuring inclusive economic growth.';
    } else if (activeTab === 'income') {
      tabSpecific = ' Employment by income category helps identify wage distribution patterns and economic stratification within the community.';
    } else if (activeTab === 'sector') {
      tabSpecific = ' Employment by sector shows the economic composition of the local workforce, essential for understanding economic diversification and resilience.';
    }
    
    return `Employment data is crucial for understanding local economic conditions, workforce trends, and planning for sustainable development.${tabSpecific} These trends support climate action planning by identifying workforce needs for green jobs and guiding equitable economic transition strategies.`;
  };

  const getEquityContent = () => {
    let tabSpecific = '';
    if (activeTab === 'sex') {
      tabSpecific = ' Gender disparities in employment can be exacerbated or addressed through climate policies. For example, green job training programs should ensure equal access for all genders, and transportation improvements should consider the different commuting patterns of men and women.';
    } else if (activeTab === 'race') {
      tabSpecific = ' Racial and ethnic employment disparities are often linked to environmental justice concerns. Communities of color frequently face both higher unemployment rates and greater exposure to environmental hazards. Climate action must address both issues simultaneously, ensuring that green job opportunities are accessible to all communities and that environmental benefits are distributed equitably.';
    } else if (activeTab === 'income') {
      tabSpecific = ' Income-based employment data reveals economic stratification. Climate policies should prioritize job creation in sectors accessible to workers across all income levels, ensuring that the transition to a green economy does not leave low-income workers behind.';
    }
    
    return `Employment equity is fundamental to climate justice. Climate action policies must consider their impact on employment opportunities, particularly for historically marginalized communities.${tabSpecific} Tracking employment trends helps ensure climate investments expand opportunity across all groups.`;
  };

  const getMethodologyContent = () => {
    return `Data Sources: LEHD Origin-Destination Employment Statistics (LODES8). Years Available: 2002-2022. Geographic Level: Data is aggregated to place (city), county, and state levels using census block group crosswalks. Methodology: LODES data is derived from state unemployment insurance records and provides detailed information about where people work (workplace). The employment data includes demographic breakdowns by sex, race/ethnicity, income, and economic sector (NAICS codes). Limitations: LODES excludes federal employees, self-employed workers, and some agricultural workers.`;
  };

  if (loading) {
    return (
      <ChartLayout
      scrollAnchorId="chart-employment"
      title="Employment Trends"
        chartType="line"
        activeView="overview"
        onViewChange={() => {}}
        chartContent={<div style={{ padding: '2rem', textAlign: 'center' }}>Loading employment data...</div>}
      />
    );
  }

  if (error) {
    return (
      <ChartLayout
      scrollAnchorId="chart-employment"
      title="Employment Trends"
        chartType="line"
        activeView="overview"
        onViewChange={() => {}}
        chartContent={<div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>Error: {error}</div>}
      />
    );
  }

  const chartTitle = isUsingCountyData && countyName 
    ? `Employment Trends (${countyName} County Data)`
    : "Employment Trends";

  const employmentChartContent = (
    <div>
      {activeTab !== 'workplace' && !embedOnly && (
        <div style={{ 
          display: 'flex', 
          gap: '8px', 
          marginBottom: '16px',
          justifyContent: 'flex-start',
          flexWrap: 'wrap'
        }}>
          <button
            onClick={() => setChartType('line')}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: chartType === 'line' ? '#3b82f6' : '#ffffff',
              color: chartType === 'line' ? '#ffffff' : '#374151',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: chartType === 'line' ? '600' : '400',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (chartType !== 'line') {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }
            }}
            onMouseLeave={(e) => {
              if (chartType !== 'line') {
                e.currentTarget.style.backgroundColor = '#ffffff';
              }
            }}
          >
            Line
          </button>
          <button
            onClick={() => setChartType('area')}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: chartType === 'area' ? '#3b82f6' : '#ffffff',
              color: chartType === 'area' ? '#ffffff' : '#374151',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: chartType === 'area' ? '600' : '400',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (chartType !== 'area') {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }
            }}
            onMouseLeave={(e) => {
              if (chartType !== 'area') {
                e.currentTarget.style.backgroundColor = '#ffffff';
              }
            }}
          >
            Area (Stacked)
          </button>
          <button
            onClick={() => setChartType('percentage')}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: chartType === 'percentage' ? '#3b82f6' : '#ffffff',
              color: chartType === 'percentage' ? '#ffffff' : '#374151',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: chartType === 'percentage' ? '600' : '400',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              if (chartType !== 'percentage') {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
              }
            }}
            onMouseLeave={(e) => {
              if (chartType !== 'percentage') {
                e.currentTarget.style.backgroundColor = '#ffffff';
              }
            }}
          >
            Percentage (Stacked Area 100%)
          </button>
        </div>
      )}
      <div style={{ position: 'relative', height: embedOnly ? '250px' : '400px' }}>
        <canvas ref={chartRef} />
      </div>
    </div>
  );

  if (embedOnly) {
    return employmentChartContent;
  }

  const chartContent = employmentChartContent;

  return (
    <ChartLayout
      scrollAnchorId="chart-employment"
      title={chartTitle}
      chartType="line"
      activeView={activeView}
      onViewChange={(view: string) => setActiveView(view as 'overview' | 'relevance' | 'equity' | 'methodology')}
      keyMetrics={
        <KeyMetrics>
          {metrics.currentWorkers && metrics.latestYear && (
            <KeyMetric
              value={metrics.currentWorkers.toLocaleString()}
              label={`Workers (${metrics.latestYear})`}
              color="#1e3a8a"
            />
          )}
          {metrics.growthRate !== null && (
            <KeyMetric
              value={`${metrics.growthRate > 0 ? '+' : ''}${metrics.growthRate.toFixed(1)}%`}
              label="Growth since 2002"
              color={metrics.growthRate > 0 ? "#10b981" : "#ef4444"}
            />
          )}
          {metrics.peakWorkers && metrics.peakYear && (
            <KeyMetric
              value={metrics.peakWorkers.toLocaleString()}
              label={`Peak (${metrics.peakYear})`}
              color="#f59e0b"
            />
          )}
        </KeyMetrics>
      }
      chartSelector={{
        value: activeTab,
        onChange: (value: string) => setActiveTab(value as any),
        options: [
          { value: 'workplace', label: 'Total Employment' },
          { value: 'sex', label: 'By Sex' },
          { value: 'race', label: 'By Race' },
          { value: 'income', label: 'By Income' },
          { value: 'sector', label: 'By Sector' },
        ],
      }}
      chartContent={chartContent}
      overviewContent={<OverviewContent title={getTabTitle()} description={getOverviewContent()} />}
      relevanceContent={<RelevanceContent title={`Relevance: ${getTabTitle()}`} description={getRelevanceContent()} />}
      equityContent={<EquityContent title={`Equity: ${getTabTitle()}`} description={getEquityContent()} />}
      methodologyContent={
        <MethodologyContent 
          title="Methodology" 
          description={getMethodologyContent()}
          dataSources={[
            'LEHD Origin-Destination Employment Statistics (LODES8)',
            'Years Available: 2002-2022',
            'Geographic Level: Place (city), County, State'
          ]}
          methodology={[
            'Data is aggregated from census block groups to places using 2020 census geography crosswalks',
            'LODES data is derived from state unemployment insurance records',
            'Includes demographic breakdowns by sex, race/ethnicity, income, and economic sector (NAICS codes)',
            'Data excludes federal employees, self-employed workers, and some agricultural workers',
            'Data is subject to disclosure avoidance techniques that may introduce small statistical noise'
          ]}
        />
      }
      showRelevanceEquity={true}
      enableComments={true}
      location={location}
    />
  );
};

export default EmploymentSection;

